import React from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { colors, layering } from '@mobi/component-library/Theme/Common'
import { Button } from '@mobi/component-library/Common/V2/Buttons/Button'
import { Icon } from '@mobi/component-library/Common/V2/Icon/Icon'
import { Modal } from '@mobi/component-library/Overlays/Modal/Modal'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import type { BetLoopProfile } from '@core/Areas/BetLoop/types'
import { postBetLoopReportProfile } from '@core/Areas/BetLoop/helpers/api'
import { handleApiErrorNotification } from '@core/Areas/BetLoop/helpers/handleApiErrorNotification'
import { trackBetLoopReportProfile } from '@core/Areas/BetLoop/helpers/analytics'

const enum LocalConstants {
  HeaderText = 'Report Profile',
  SubHeaderText = 'We have zero tolerance for abuse or spam. Hit “Confirm” to report this profile so our team can review it. Reports are handled privately and never shared with the person involved.',
}

export const ReportProfileButton: React.FC<{ onClick(): void }> = ({ onClick }) => (
  <Button size='sm' color='secondary_outline' onClick={onClick}>
    <div style={{ color: colors.error[600] }}>
      <Icon name='LineAnnotationAlert' color='currentColor' />
      <span>Report Profile</span>
    </div>
  </Button>
)

export const ReportProfileModal: React.FC<{
  isOpen: boolean
  onDismiss(): void
  accountId: BetLoopProfile['id']
}> = ({ isOpen, onDismiss, accountId }) => {
  const { mutate, isPending } = useMutation({
    mutationFn: () => postBetLoopReportProfile(accountId),
    onSuccess: () => {
      onDismiss()
      addToast({
        id: 'bet-loop-report-profile',
        icon: 'success',
        title: 'Thanks! Profile successfully reported',
        theme: 'dark',
        isDismissable: true,
      })
    },
    onError: e => handleApiErrorNotification(e),
  })

  const onConfirmReportModal = () => {
    trackBetLoopReportProfile()
    mutate()
  }

  return createPortal(
    <Modal
      isOpen={isOpen}
      headerIcon='warning'
      headerTitle={LocalConstants.HeaderText}
      headerSubtitle={LocalConstants.SubHeaderText}
      onDismiss={onDismiss}
      zIndex={layering.overlayHigh}
      modalStyle={{ maxWidth: '44rem' }}
      footerComponent={
        <>
          <Button
            size='md'
            isFullWidth
            color='secondary_grey'
            onClick={onDismiss}
            data-testid='BetLoopReporting.Cancel'
          >
            Cancel
          </Button>

          <Button
            size='md'
            isFullWidth
            color='primary'
            onClick={onConfirmReportModal}
            isLoading={isPending}
            data-testid='BetLoopReporting.Confirm'
          >
            Confirm
          </Button>
        </>
      }
    />,
    document.body
  )
}
